import { useMemo, useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Checkbox,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Check as CheckIcon, Done as DoneIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { saveLease } from '@/data';
import { getLongNumberStr } from '@/helpers/common';
import { PricingWizardContextValue } from '../types';
import { setHighlightedUnits, setBuildingVisual } from '@/features/project/projectSlice';

interface ApprovalStepProps {
  units: Unit[];
  draftLeases: Lease[];
  wizard: PricingWizardContextValue;
  onComplete: () => void;
}

export function ApprovalStep({
  units,
  draftLeases,
  wizard,
  onComplete,
}: ApprovalStepProps) {
  const dispatch = useDispatch();
  const { state, actions } = wizard;
  const [approving, setApproving] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState<string | null>(null);
  const [approvedCount, setApprovedCount] = useState(0);

  // Create unit map
  const unitMap = useMemo(() => {
    const map = new Map<string, Unit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  // Filter draft leases to only those created in this wizard session
  const wizardDraftLeases = useMemo(() => {
    return draftLeases.filter((draft) => state.draftLeaseIds.has(draft.id));
  }, [draftLeases, state.draftLeaseIds]);

  // Highlight units with draft leases in the building visualization
  useEffect(() => {
    if (wizardDraftLeases.length === 0) {
      dispatch(setHighlightedUnits([]));
      return;
    }

    // Build apartment data - highlight selected for approval in green, others in default
    const apartmentData = wizardDraftLeases.map((draft) => {
      const isSelected = state.selectedForApproval.has(draft.id);
      return {
        apartmentId: draft.unitId,
        value: isSelected ? 1 : 0.5,
        label: isSelected ? 'Selected' : 'Pending',
      };
    });

    dispatch(setBuildingVisual({ apartmentData, visualizer: undefined }));

    setTimeout(() => {
      dispatch(setHighlightedUnits(apartmentData));
    }, 500);

    return () => {
      dispatch(setHighlightedUnits([]));
    };
  }, [wizardDraftLeases, state.selectedForApproval, dispatch]);

  const selectedCount = state.selectedForApproval.size;
  const totalCount = wizardDraftLeases.length;

  // Handle select/deselect all
  const handleSelectAll = () => {
    if (selectedCount === totalCount) {
      actions.clearApprovalSelection();
    } else {
      actions.selectAllForApproval();
    }
  };

  // Handle toggle individual selection
  const handleToggleSelection = (leaseId: string) => {
    actions.toggleApprovalSelection(leaseId);
  };

  // Handle approve selected
  const handleApprove = async () => {
    if (selectedCount === 0) return;

    setApproving(true);
    setApprovalMessage(null);
    let successCount = 0;

    try {
      for (const leaseId of state.selectedForApproval) {
        const draft = wizardDraftLeases.find((d) => d.id === leaseId);
        if (draft) {
          // Note: 'approved' status extends the Lease type - the backend should handle this
          const updatedLease = {
            ...draft,
            status: 'approved' as const,
          } as any;
          await saveLease(updatedLease);
          successCount++;
        }
      }

      setApprovedCount(successCount);
      setApprovalMessage(`Successfully approved ${successCount} draft lease(s)`);
      actions.clearApprovalSelection();
    } catch (error) {
      console.error('Error approving leases:', error);
      setApprovalMessage('Error approving some leases');
    } finally {
      setApproving(false);
    }
  };

  // Check if all drafts are approved
  const allApproved = useMemo(() => {
    return wizardDraftLeases.every((draft) => (draft as any).status === 'approved');
  }, [wizardDraftLeases]);

  return (
    <div className="approval-step h-full flex flex-col px-8 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Typography variant="h6">Approve Draft Leases</Typography>
          <Typography variant="body2" color="textSecondary">
            Select and approve draft leases to mark them as reviewed
          </Typography>
        </div>
        <div className="flex items-center gap-4">
          <Typography variant="body2">
            {selectedCount} of {totalCount} selected
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
            disabled={totalCount === 0}
          >
            {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={selectedCount === 0 || approving}
            startIcon={approving ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {approving ? 'Approving...' : `Approve (${selectedCount})`}
          </Button>
        </div>
      </div>

      {approvalMessage && (
        <Alert
          severity={approvalMessage.includes('Error') ? 'error' : 'success'}
          className="mb-4"
          onClose={() => setApprovalMessage(null)}
        >
          {approvalMessage}
        </Alert>
      )}

      {/* Draft lease list */}
      {wizardDraftLeases.length > 0 ? (
        <div className="flex-1 overflow-auto">
          <div className="space-y-2">
            {wizardDraftLeases.map((draft) => {
              const draftUnit = unitMap.get(draft.unitId);
              const isSelected = state.selectedForApproval.has(draft.id);
              const isApproved = (draft as any).status === 'approved';

              return (
                <Paper
                  key={draft.id}
                  className={`p-4 flex items-center gap-4 ${
                    isApproved ? 'bg-green-900/20 border-green-600/30' : ''
                  }`}
                  variant="outlined"
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleSelection(draft.id)}
                    disabled={isApproved}
                  />
                  <div className="flex-1">
                    <Typography variant="body1" className="font-semibold">
                      {draftUnit?.name || draft.unitId}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {draftUnit?.numBedrooms}BR, {draftUnit?.sqft} sqft
                    </Typography>
                  </div>
                  <div className="text-right">
                    <Typography variant="body2" color="textSecondary">
                      Proposed Rent
                    </Typography>
                    <Typography variant="body1" color="primary">
                      ${getLongNumberStr(draft.monthlyRent)}
                    </Typography>
                  </div>
                  <div className="text-right">
                    <Typography variant="body2" color="textSecondary">
                      Concessions
                    </Typography>
                    <Typography variant="body1">
                      ${getLongNumberStr(draft.totalConcessionAmount || 0)}
                    </Typography>
                  </div>
                  <div className="w-24 text-center">
                    {isApproved ? (
                      <div className="flex items-center justify-center gap-1 text-green-500">
                        <DoneIcon fontSize="small" />
                        <Typography variant="body2">Approved</Typography>
                      </div>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Pending
                      </Typography>
                    )}
                  </div>
                </Paper>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Typography variant="body1" color="textSecondary" className="mb-2">
              No draft leases to approve
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Create draft leases in the pricing step first
            </Typography>
          </div>
        </div>
      )}

      {/* Completion message */}
      {allApproved && wizardDraftLeases.length > 0 && (
        <Paper className="mt-6 p-6 bg-green-900/20 text-center" elevation={0}>
          <DoneIcon className="text-green-500 mb-2" sx={{ fontSize: 48 }} />
          <Typography variant="h6" className="mb-2">
            All Draft Leases Approved!
          </Typography>
          <Typography variant="body2" color="textSecondary" className="mb-4">
            You can now close this wizard and find your approved draft leases in the
            Draft Leases report.
          </Typography>
          <Button variant="contained" onClick={onComplete}>
            Finish and Return to Building
          </Button>
        </Paper>
      )}
    </div>
  );
}
